/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-router components
import { Link } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// Redux
import { useDispatch } from "react-redux";
import { fetchLogout } from "actions/login";

// @mui material components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Material Dashboard 3 PRO React examples
import NotificationItem from "examples/Items/NotificationItem";

// Custom styles for DashboardNavbar
import {
	navbar,
	navbarContainer,
	navbarRow,
	navbarIconButton,
	navbarDesktopMenu,
	navbarMobileMenu
} from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 3 PRO React context
import {
	useMaterialUIController,
	setTransparentNavbar,
	setMiniSidenav
} from "context";

function DashboardNavbar({
	absolute = false,
	light = false,
	isMini = false,
	title = ""
}) {
	const [navbarType, setNavbarType] = useState();
	const [controller, dispatch] = useMaterialUIController();
	const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;
	const [openMenu, setOpenMenu] = useState(false);
	const reduxDispatch = useDispatch();
	// Users signed in with Facebook have no local password to change
	const isStrategyLocal = localStorage.getItem("strategy") !== "facebook";

	useEffect(() => {
		// Setting the navbar type
		if (fixedNavbar) {
			setNavbarType("sticky");
		} else {
			setNavbarType("static");
		}

		// A function that sets the transparent state of the navbar.
		function handleTransparentNavbar() {
			setTransparentNavbar(
				dispatch,
				(fixedNavbar && window.scrollY === 0) || !fixedNavbar
			);
		}

		/** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
		window.addEventListener("scroll", handleTransparentNavbar);

		// Call the handleTransparentNavbar function to set the state with the initial value.
		handleTransparentNavbar();

		// Remove event listener on cleanup
		return () => window.removeEventListener("scroll", handleTransparentNavbar);
	}, [dispatch, fixedNavbar]);

	const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
	const handleOpenMenu = event => setOpenMenu(event.currentTarget);
	const handleCloseMenu = () => setOpenMenu(false);
	const handleLogout = () => {
		handleCloseMenu();
		reduxDispatch(fetchLogout());
	};

	// Render the account menu
	const renderMenu = () => (
		<Menu
			anchorEl={openMenu}
			anchorReference={null}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "left"
			}}
			open={Boolean(openMenu)}
			onClose={handleCloseMenu}
			sx={{ mt: 2 }}
		>
			<NotificationItem
				component={Link}
				to="/team/profile"
				onClick={handleCloseMenu}
				icon={<Icon>person</Icon>}
				title="Profil"
			/>
			{isStrategyLocal && (
				<NotificationItem
					component={Link}
					to="/team/change-password"
					onClick={handleCloseMenu}
					icon={<Icon>lock</Icon>}
					title="Mot de passe"
				/>
			)}
			<NotificationItem
				onClick={handleLogout}
				icon={<Icon>logout</Icon>}
				title="Se déconnecter"
			/>
		</Menu>
	);

	// Styles for the navbar icons
	const iconsStyle = ({
		palette: { dark, white, text },
		functions: { rgba }
	}) => ({
		color: () => {
			let colorValue = light || darkMode ? white.main : dark.main;

			if (transparentNavbar && !light) {
				colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
			}

			return colorValue;
		}
	});

	return (
		<AppBar
			position={absolute ? "absolute" : navbarType}
			color="inherit"
			sx={theme =>
				navbar(theme, { transparentNavbar, absolute, light, darkMode })
			}
		>
			<Toolbar sx={theme => navbarContainer(theme)}>
				<MDBox color="inherit" sx={theme => navbarRow(theme, { isMini })}>
					<IconButton
						sx={navbarDesktopMenu}
						onClick={handleMiniSidenav}
						size="small"
						disableRipple
					>
						<Icon fontSize="medium" sx={iconsStyle}>
							{miniSidenav ? "menu_open" : "menu"}
						</Icon>
					</IconButton>
					<MDBox ml={{ xs: 0, xl: 1 }}>
						<MDTypography
							variant="h6"
							fontWeight="bold"
							color={light ? "white" : "dark"}
							noWrap
						>
							{title}
						</MDTypography>
					</MDBox>
				</MDBox>
				{isMini ? null : (
					<MDBox sx={theme => navbarRow(theme, { isMini })}>
						<MDBox color={light ? "white" : "inherit"}>
							<IconButton
								size="small"
								disableRipple
								color="inherit"
								sx={navbarMobileMenu}
								onClick={handleMiniSidenav}
							>
								<Icon sx={iconsStyle} fontSize="medium">
									{miniSidenav ? "menu_open" : "menu"}
								</Icon>
							</IconButton>
							<IconButton
								size="small"
								disableRipple
								color="inherit"
								sx={navbarIconButton}
								aria-controls="account-menu"
								aria-haspopup="true"
								onClick={handleOpenMenu}
							>
								<Icon sx={iconsStyle}>account_circle</Icon>
							</IconButton>
							{renderMenu()}
						</MDBox>
					</MDBox>
				)}
			</Toolbar>
		</AppBar>
	);
}

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
	absolute: PropTypes.bool,
	light: PropTypes.bool,
	isMini: PropTypes.bool,
	title: PropTypes.string
};

export default DashboardNavbar;
